// shared/src/schemas/rank-probe.ts
// Zod row-guards for the Track A crypto rank-IC probe tables (DDL:
// migrations/0066_rank_probe_and_target_calibration.sql). Plain z.object (no
// .strict / no effects) so the schema-vs-ddl drift detector's getZodFields()
// can read .shape. Field names mirror the DDL columns 1:1.
//
// docs/cross-sectional-rank-ic-2026-06-25-brief-revision.md §4.

import { z } from 'zod';

// ── Per-ticker prediction item (the JSON shape inside predictions_json) ──────
// NOT a DDL table — the structured form the q-rank-probe LLM emits per cluster
// ticker. Direction is up/down/flat (a forecast), distinct from signals'
// long/short. Reused by the prompt emit + the scorer's realized comparison.
export const RankProbeDirectionEnum = z.enum(['up', 'down', 'flat']);
export const RankProbeConfidenceEnum = z.enum(['HIGH', 'MED', 'LOW']);

export const RankProbeTickerPredictionSchema = z.object({
  ticker:              z.string().min(1),
  direction:           RankProbeDirectionEnum,
  expected_change_pct: z.number(),
  confidence:          RankProbeConfidenceEnum,
});
export type RankProbeTickerPrediction = z.infer<typeof RankProbeTickerPredictionSchema>;

// ── Realized item (JSON shape inside rank_probe_outcomes.realized_json) ──────
// numeraire_relative_return = (1+fwd_return)/(1+fwd_return_numeraire)−1, where the
// numeraire is the basket's first cluster member ($BTC for crypto, SPY for
// equity_wide). Strips the dominant common factor so a pure beta-rider scores ρ≈0.
export const RankProbeRealizedTickerSchema = z.object({
  ticker:                    z.string().min(1),
  fwd_return:                z.number(),
  numeraire_relative_return: z.number(),
  realized_direction:        RankProbeDirectionEnum,
});
export type RankProbeRealizedTicker = z.infer<typeof RankProbeRealizedTickerSchema>;

// ── rank_probe_predictions (10 cols) ────────────────────────────────────────
export const RankProbePredictionSchema = z.object({
  id:               z.string(),
  brief_id:         z.string(),
  basket_id:        z.string(),
  horizon_days:     z.number().int(),
  signal_at:        z.string(),
  resolves_at:      z.string(),
  predictions_json: z.string(),
  n_committed:      z.number().int(),
  llm_call_id:      z.string().nullable(),
  created_at:       z.string(),
});
export type RankProbePredictionRow = z.infer<typeof RankProbePredictionSchema>;

// ── rank_probe_outcomes (12 cols) ───────────────────────────────────────────
export const RankProbeOutcomeSchema = z.object({
  id:                     z.string(),
  prediction_id:          z.string(),
  basket_id:              z.string(),
  realized_json:          z.string(),
  spearman_rho:           z.number(),
  benchmark_momentum_rho: z.number(),
  n_scored:               z.number().int(),
  regime_label:           z.string().nullable(),
  vol_bucket:             z.string().nullable(),
  non_overlapping:        z.number().int(),
  scorer_version:         z.string(),
  scored_at:              z.string(),
});
export type RankProbeOutcomeRow = z.infer<typeof RankProbeOutcomeSchema>;
